"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import "./enterprise-intelligence.css";
import dontStartWithAiImage from "/assets/images/don't-start-with-ai.jpg";
import enterpriseIntelligenceImage from "/assets/images/enterprise-intelligence.png";

/*
  NOTE: This file is now a client component ("use client"), so the
  `metadata` export can no longer live here. Move it to the route's
  layout.tsx (see the snippet in the chat message).
*/

const businessQuestions = [
  "Why did margin fall?",
  "Why is inventory increasing?",
  "Which customers are becoming less profitable?",
  "Which assets are likely to fail?",
  "Where is cash getting trapped?",
  "Which exceptions need attention today?",
];

const intelligenceLevels = [
  { number: "01", title: "Detect", text: "What changed?" },
  { number: "02", title: "Explain", text: "Why did it change?" },
  { number: "03", title: "Predict", text: "What is likely to happen?" },
  { number: "04", title: "Recommend", text: "What should we consider doing?" },
  { number: "05", title: "Act", text: "How do we turn the decision into workflow and action?" },
];

const enterpriseAreas = [
  {
    title: "Supply Chain & Assets",
    items: ["Inventory", "Forecasting", "Stock-outs", "Maintenance", "Working Capital"],
    icon: "supply",
  },
  {
    title: "Finance & Performance",
    items: ["Margin", "Profitability", "Cash", "Cost", "Planning"],
    icon: "finance",
  },
  {
    title: "Customer & Revenue",
    items: ["Customer Value", "Pricing", "Schemes", "Growth", "Receivables"],
    icon: "customer",
  },
  {
    title: "Vendor & Spend",
    items: ["Spend", "Pricing", "Supplier Performance", "Risk", "Leakage"],
    icon: "vendor",
  },
  {
    title: "People & Talent",
    items: ["Skills", "Productivity", "Hiring", "Retention", "Workforce"],
    icon: "people",
  },
  {
    title: "Office of CXO",
    items: ["Exceptions", "Performance", "Risk", "Priorities", "Actions"],
    icon: "cxo",
  },
];

const intelligenceMethods = [
  { title: "Business Rules", icon: "rules" },
  { title: "Analytics", icon: "analytics" },
  { title: "Statistics", icon: "statistics" },
  { title: "ML / XGBoost", icon: "ml" },
  { title: "Optimisation", icon: "optimisation" },
  { title: "LLMs", icon: "llm" },
  { title: "Agents", icon: "agents", wide: true },
];

const valueSteps = [
  { title: "Insight", icon: "⌕" },
  { title: "Opportunity Identified", icon: "♧" },
  { title: "Business Validated", icon: "▤" },
  { title: "Action Approved", icon: "✓" },
  { title: "Action Executed", icon: "⚙" },
  { title: "Result Measured", icon: "▥" },
  { title: "Value Realised", icon: "♜", final: true },
];

const recurringProblems = [
  "Why is inventory increasing when sales aren’t?",
  "Why did margin fall?",
  "Where is our cash actually stuck?",
  "Which customers generate revenue but destroy margin?",
  "Are we paying different prices for the same thing?",
  "Which assets are likely to fail?",
  "Where are our critical skill gaps?",
  "Which five exceptions require my attention now?",
];

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400): CSSProperties =>
  ({ "--ei-delay": `${Math.min(i * step, max)}ms` } as CSSProperties);

/* ---------- Icons ---------- */

function ContextIcon({ type }: { type: string }) {
  const common = {
    viewBox: "0 0 32 32",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  switch (type) {
    case "data":
      return (
        <svg {...common}>
          <ellipse cx="16" cy="7" rx="8" ry="3.5" />
          <path d="M8 7v7c0 1.9 3.6 3.5 8 3.5s8-1.6 8-3.5V7" />
          <path d="M8 14v7c0 1.9 3.6 3.5 8 3.5s8-1.6 8-3.5v-7" />
        </svg>
      );
    case "rules":
      return (
        <svg {...common}>
          <rect x="7" y="6" width="18" height="20" rx="2.5" />
          <path d="M11 11h10M11 16h10M11 21h6" />
          <circle cx="9.5" cy="11" r=".8" fill="currentColor" stroke="none" />
          <circle cx="9.5" cy="16" r=".8" fill="currentColor" stroke="none" />
          <circle cx="9.5" cy="21" r=".8" fill="currentColor" stroke="none" />
        </svg>
      );
    case "process":
      return (
        <svg {...common}>
          <rect x="5" y="7" width="7" height="6" rx="1.5" />
          <rect x="20" y="7" width="7" height="6" rx="1.5" />
          <rect x="12.5" y="20" width="7" height="6" rx="1.5" />
          <path d="M12 10h8M23.5 13v3.5l-4 3.5M8.5 13v3.5l7.5 3.5" />
        </svg>
      );
    case "industry":
      return (
        <svg {...common}>
          <path d="M5 26V12l7-5v5l7-5v5l8-5v19" />
          <path d="M9 26v-5h4v5M17 26v-7h4v7M24 26v-4h2" />
        </svg>
      );
    case "history":
      return (
        <svg {...common}>
          <path d="M7 11a10 10 0 1 1-1 8" />
          <path d="M6 6v6h6" />
          <path d="M16 11v6l4 2" />
        </svg>
      );
    case "relationships":
      return (
        <svg {...common}>
          <circle cx="8" cy="16" r="3" />
          <circle cx="24" cy="9" r="3" />
          <circle cx="24" cy="23" r="3" />
          <path d="m10.8 14.8 10.4-4.5M10.8 17.2l10.4 4.5" />
        </svg>
      );
    default:
      return null;
  }
}

function MethodIcon({ type }: { type: string }) {
  const common = {
    viewBox: "0 0 32 32",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  switch (type) {
    case "rules":
      return (
        <svg {...common}>
          <rect x="6" y="5" width="20" height="22" rx="2.5" />
          <path d="M10 10h12M10 15h12M10 20h7" />
        </svg>
      );
    case "analytics":
      return (
        <svg {...common}>
          <path d="M6 26V17M13 26V11M20 26V6M27 26V14" />
          <path d="M5 26h23" />
          <path d="m7 13 6-5 7 2 6-6" />
        </svg>
      );
    case "statistics":
      return (
        <svg {...common}>
          <path d="M16 5v22M5 16h22" />
          <path d="M10 10h12v12H10z" />
          <path d="M10 10 22 22M22 10 10 22" />
        </svg>
      );
    case "ml":
      return (
        <svg {...common}>
          <circle cx="16" cy="16" r="4" />
          <circle cx="7" cy="9" r="2.5" />
          <circle cx="25" cy="9" r="2.5" />
          <circle cx="7" cy="23" r="2.5" />
          <circle cx="25" cy="23" r="2.5" />
          <path d="m9.2 10.4 3.5 3.2M22.8 10.4l-3.5 3.2M9.2 21.6l3.5-3.2M22.8 21.6l-3.5-3.2" />
        </svg>
      );
    case "optimisation":
      return (
        <svg {...common}>
          <circle cx="16" cy="16" r="5" />
          <path d="M16 5v3M16 24v3M5 16h3M24 16h3M8.2 8.2l2.1 2.1M21.7 21.7l2.1 2.1M23.8 8.2l-2.1 2.1M10.3 21.7l-2.1 2.1" />
          <circle cx="16" cy="16" r="10" />
        </svg>
      );
    case "llm":
      return (
        <svg {...common}>
          <rect x="7" y="7" width="18" height="18" rx="2" />
          <path d="M11 11h10M11 16h10M11 21h6" />
          <path d="M4 12v-1M4 20v-1M28 12v-1M28 20v-1" />
        </svg>
      );
    case "agents":
      return (
        <svg {...common}>
          <circle cx="16" cy="16" r="7" />
          <circle cx="16" cy="16" r="2.5" />
          <path d="M16 4v5M16 23v5M4 16h5M23 16h5" />
          <path d="m7.5 7.5 3.5 3.5M21 21l3.5 3.5M24.5 7.5 21 11M11 21l-3.5 3.5" />
        </svg>
      );
    default:
      return null;
  }
}

function Arrow() {
  return <span aria-hidden="true">→</span>;
}

function AreaIcon({ type }: { type: string }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  switch (type) {
    case "supply":
      return (
        <svg {...common}>
          <path d="M3.5 7.5 12 3l8.5 4.5L12 12 3.5 7.5Z" />
          <path d="M3.5 12 12 16.5 20.5 12" />
          <path d="M3.5 16.5 12 21l8.5-4.5" />
        </svg>
      );
    case "finance":
      return (
        <svg {...common}>
          <path d="M4 19V9" />
          <path d="M10 19V5" />
          <path d="M16 19v-7" />
          <path d="M3 19.5h18" />
          <path d="m4 7 5-3 6 3 5-3" />
        </svg>
      );
    case "customer":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="3.2" />
          <path d="M5.5 20c.6-3.5 2.8-5.3 6.5-5.3s5.9 1.8 6.5 5.3" />
          <path d="M18 7.5h2.5" />
          <path d="M19.25 6.25v2.5" />
        </svg>
      );
    case "vendor":
      return (
        <svg {...common}>
          <path d="M4 20V8.5L12 4l8 4.5V20" />
          <path d="M8 20v-5h8v5" />
          <path d="M8 10h.01M12 10h.01M16 10h.01" />
          <path d="M3 20h18" />
        </svg>
      );
    case "people":
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="2.8" />
          <circle cx="17" cy="9" r="2.2" />
          <path d="M3.8 20c.5-3.4 2.3-5.1 5.2-5.1s4.7 1.7 5.2 5.1" />
          <path d="M14.5 15.2c2.9.1 4.5 1.5 4.9 4.8" />
        </svg>
      );
    case "cxo":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8.2" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 3.8v2M12 18.2v2M3.8 12h2M18.2 12h2" />
        </svg>
      );
    default:
      return null;
  }
}

/* ---------- Page ---------- */

export default function EnterpriseIntelligencePage() {
  const ref = useScrollReveal();

  return (
    <main className="ei-page" ref={ref}>
      {/* HERO */}
      <section className="ei-hero">
        <div className="ei-hero-inner">
          <div className="ei-hero-copy">
            <h1 data-reveal style={delay(0)}>
              Turn Enterprise Data
              <br />
              Into Better Decisions.
            </h1>
            <p className="ei-hero-description" data-reveal style={delay(1, 150)}>
              Orane combines business context, trusted enterprise data,
              analytics, AI and agents to help organisations understand what
              happened, why it happened, what may happen next — and what to do
              about it.
            </p>
            <div className="ei-hero-buttons" data-reveal style={delay(2, 150)}>
              <Link href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary ei-btn">
                Tell Us Your Problem
              </Link>
              <a href="#how-it-works" className="fp-buttons fp-hero-outline-btn ei-btn ei-btn-second">
                See How It Works
              </a>
            </div>
          </div>

          {/*
          <div className="ei-hero-stack" aria-label="From insight to impact">
            {["BUSINESS", "CONTEXT", "DATA", "INTELLIGENCE", "DECISION", "ACTION", "VALUE"].map(
              (item, index) => (
                <div
                  className={`ei-stack-item ei-stack-${index + 1}`}
                  key={item}
                >
                  {item}
                </div>
              )
            )}
            <div className="ei-stack-arrow">↓</div>
            <p>From insight<br />to impact.</p>
          </div>
          */}
        </div>
      </section>

      {/* BUSINESS QUESTION / INTELLIGENCE MODEL */}
      <section className="ei-section ei-intelligence-intro" id="how-it-works">
        <div className="ei-intro-copy ei-reveal" data-reveal>
          <p className="eyebrow">AI IS NOT THE STARTING POINT</p>
          <h2>Start With the<br />Business Question.</h2>
          <p>An enterprise rarely starts its day asking “Where can we use AI?”</p>
          <p>It asks real business questions:</p>
          <ul className="ei-check-list">
            {businessQuestions.map((question) => (
              <li key={question}>
                <span>✓</span>
                {question}
              </li>
            ))}
          </ul>
          <strong>Orane starts there.</strong>
        </div>

        <div className="ei-flow-card ei-reveal" data-reveal style={delay(1, 150)}>
          <p className="ei-mini-title">FROM DATA TO INTELLIGENCE</p>
          <div className="ei-source-row">
            {["ERP", "CRM", "SAP", "HR", "PLANNING", "EXTERNAL"].map((source) => (
              <div className="ei-source" key={source}>
                <span>▧</span>
                {source}
              </div>
            ))}
          </div>
          <div className="ei-flow-box dark">TRUSTED DATA</div>
          <div className="ei-flow-arrow">↓</div>
          <div className="ei-flow-box">BUSINESS CONTEXT</div>
          <div className="ei-flow-arrow">↓</div>
          <div className="ei-intelligence-box">
            <b>ENTERPRISE INTELLIGENCE</b>
            <div>
              {["Detect", "Explain", "Predict", "Recommend", "Act"].map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
          <div className="ei-flow-arrow">↓</div>
          <div className="ei-flow-box">HUMAN DECISION</div>
          <div className="ei-flow-arrow">↓</div>
          <div className="ei-flow-box">ACTION</div>
          <div className="ei-flow-arrow">↓</div>
          <div className="ei-flow-box success">MEASURABLE VALUE</div>
        </div>

        <aside className="ei-levels-card ei-reveal" data-reveal>
          <p className="ei-mini-title">FIVE LEVELS OF INTELLIGENCE</p>
          <h3 className="ei-levels-heading">From Detection to Action.</h3>
          <div className="ei-levels-list">
            {intelligenceLevels.map((level, i) => (
              <div className="ei-level" key={level.number} data-reveal style={delay(i, 100)}>
                <span>{level.number}</span>
                <div>
                  <b>{level.title}</b>
                  <small>{level.text}</small>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </section>

      {/* INTELLIGENCE ACROSS THE ENTERPRISE */}
      <section className="ei-section ei-enterprise">
        <div className="ei-section-heading ei-reveal" data-reveal>
          <div>
            <p className="eyebrow">INTELLIGENCE ACROSS THE ENTERPRISE</p>
            <h2>Different business areas. A common intelligence layer.</h2>
          </div>
          <Link href="/52problem#all-problems" className="ei-link">
            Explore by Business Problem <Arrow />
          </Link>
        </div>

        <div className="ei-area-grid ei-stagger">
          {enterpriseAreas.map((area, i) => (
            <article className="ei-area-card" key={area.title} data-reveal style={delay(i, 80, 480)}>
              <span className="ei-area-icon"><AreaIcon type={area.icon} /></span>
              <h3>{area.title}</h3>
              <ul className="ei-area-list">
                {area.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* CONTEXT / METHODS */}
      <section className="ei-section ei-context-grid">
        <div className="ei-context-card ei-reveal" data-reveal>
          <p className="eyebrow">INTELLIGENCE NEEDS BUSINESS CONTEXT</p>

          <h3 className="ei-context-heading">
            Business Context Makes Data Meaningful.
          </h3>

          <p className="ei-context-lead">
            A model can find a pattern. An enterprise needs to understand what
            that pattern means.
          </p>
          <div className="ei-context-items">
            {[
              ["DATA", "data"],
              ["BUSINESS RULES", "rules"],
              ["PROCESS", "process"],
              ["INDUSTRY CONTEXT", "industry"],
              ["HISTORY", "history"],
              ["RELATIONSHIPS", "relationships"],
            ].map(([item, icon]) => (
              <div key={item}>
                <span className="ei-context-icon">
                  <ContextIcon type={icon} />
                </span>
                <b>{item}</b>
              </div>
            ))}
          </div>
          <div className="ei-context-result">ENTERPRISE INTELLIGENCE</div>
          {/*
          <div className="ei-example">
            <p>
              For FMCG, that context may be Brand → SKU → Distributor → Outlet
              → Scheme.
            </p>
            <p>For Utilities, Consumer → Meter → Feeder → Substation.</p>
            <p>For Finance, Revenue → Cost → Margin → Cash → Working Capital.</p>
          </div>
          */}
        </div>

        <div className="ei-method-card ei-reveal" data-reveal style={delay(1, 140)}>
          <p className="eyebrow">DIFFERENT PROBLEMS NEED DIFFERENT INTELLIGENCE</p>

          <h3 className="ei-context-heading">
            The Right Intelligence for Every Problem.
          </h3>

          <p className="ei-context-lead">
            Use AI where it creates value. Use simpler methods where they work
            better.
          </p>
          <div className="ei-method-grid">
            {intelligenceMethods.map((method) => (
              <div
                className={`ei-method ${method.wide ? "wide" : ""}`}
                key={method.title}
              >
                <span className="ei-method-icon">
                  <MethodIcon type={method.icon} />
                </span>
                <b>{method.title}</b>
              </div>
            ))}
          </div>
          <div className="ei-method-result">RIGHT METHOD FOR<br />THE BUSINESS PROBLEM</div>
        </div>

        <aside className="ei-aria-card ei-reveal" data-reveal style={delay(2, 140)}>
          <p className="eyebrow">ASK YOUR ENTERPRISE</p>
          <h3>Get answers, not just data.</h3>
          <ul>
            {[
              "Ask a business question",
              "Understand context",
              "Query trusted data",
              "Identify exceptions",
              "Analyse drivers",
              "Explain",
              "Predict",
              "Recommend",
              "Human decision",
              "Action",
            ].map((item, index) => (
              <li key={item}>
                <span>{index + 1}</span>
                {item}
              </li>
            ))}
          </ul>
          <div className="ei-aria-box">
            <strong>ARIA</strong>
            <span>Ask Your Enterprise.</span>
          </div>
        </aside>
      </section>

      {/* VALUE JOURNEY */}
      <section className="ei-section ei-value-section">
        <div className="ei-section-heading ei-reveal" data-reveal>
          <div>
            <p className="eyebrow">FROM INTELLIGENCE TO VALUE</p>
            <h2>Intelligence only matters when something improves.</h2>
          </div>
        </div>
        <div className="ei-value-flow ei-stagger">
          {valueSteps.map((step, i) => (
            <div
              className={`ei-value-step ${step.final ? "final" : ""}`}
              key={step.title}
              data-reveal
              style={delay(i, 80, 560)}
            >
              <span>{step.icon}</span>
              <b>{step.title}</b>
            </div>
          ))}
        </div>
      </section>

      {/*
      // BUSINESS PROBLEMS
      <section className="ei-section ei-problems">
        <div className="ei-section-heading ei-reveal">
          <div>
            <p className="eyebrow">START WITH A QUESTION YOUR BUSINESS IS ALREADY ASKING</p>
            <h2>Explore some of the 52 business problems we help solve.</h2>
          </div>
          <Link href="/whatwedo">
            Explore All 52 Business Problems <Arrow />
          </Link>
        </div>
        <div className="ei-problem-grid ei-stagger">
          {recurringProblems.map((problem) => (
            <div className="ei-problem-card" key={problem}>
              {problem}
            </div>
          ))}
        </div>
      </section>

      */}

      {/* FINAL CTA */}
      <section className="ei-final-cta">
        <div className="ei-final-heading">
          <p className="eyebrow" data-reveal>A MORE VALUABLE TOMORROW. TOGETHER.</p>
          <h2 data-reveal style={delay(1, 120)}>Don’t Start With AI.<br />Start With a Business Problem.</h2>
        </div>
        <div className="ei-final-copy">
          <p data-reveal style={delay(2, 120)}>
            Bring us a problem, a decision or an exception that matters. We’ll
            work backwards from the business outcome to the context, data and
            intelligence required.
          </p>
          <Link href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary ei-btn" data-reveal style={delay(3, 120)}>
            Tell Us Your Problem
          </Link>
        </div>
      </section>
    </main>
  );
}